/**
 * Admin notes + attachments (COL-245).
 *
 * A reusable, chronological feed of internal operator notes. Scope it to a
 * tenant (bookingId + userId) for the profile "Notes" tab, or to a specific
 * payment (paymentId) for per-payment notes. Each note can be text, a file, or
 * both. Admin-only (enforced by RLS on `admin_notes`).
 */
import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Paperclip,
  Loader2,
  Trash2,
  FileText,
  StickyNote,
  Send,
  Eye,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { noteAttachmentHref } from "@/hooks/v2/useEntityNotes";
import { openPendingDocumentTab } from "@/lib/open-document-tab";
import {
  useAdminNotes,
  useAddAdminNote,
  useDeleteAdminNote,
  type AdminNoteRow,
} from "@/hooks/v2/useProperties";

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function authorLabel(note: AdminNoteRow, fallback: string): string {
  return note.author?.name || note.author?.email || fallback;
}

export function AdminNotesSection({
  bookingId = null,
  userId = null,
  paymentId = null,
  title,
  compact = false,
}: {
  bookingId?: number | null;
  userId?: string | null;
  paymentId?: string | null;
  title?: string;
  compact?: boolean;
}) {
  const { user, isSuperAdmin } = useAuth();
  const { t } = useLanguage();
  const resolvedTitle = title ?? t("admin.notes.section.title", "Notes");
  /**
   * Since 20260812100000 the database refuses an UPDATE or DELETE of another
   * admin's note. Offering the button anyway would turn a rule into an error
   * message — so the affordance follows the same rule the trigger enforces.
   * A note whose author's account is gone (created_by NULL) stays clearable.
   */
  const canDelete = (note: AdminNoteRow) =>
    isSuperAdmin || !note.created_by || note.created_by === user?.id;
  const scope = { bookingId, userId, paymentId };
  const { data: notes = [], isLoading } = useAdminNotes(scope);
  const addNote = useAddAdminNote();
  const deleteNote = useDeleteAdminNote();

  const [body, setBody] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const busy = uploading || addNote.isPending;
  const canSubmit = (body.trim().length > 0 || !!pendingFile) && !busy;

  // A note's file_url is the public tenant-documents URL stored at upload.
  // That bucket is going private (COL-436), so the link is signed on click.
  async function openAttachment(note: AdminNoteRow, download: boolean) {
    const tab = openPendingDocumentTab();
    try {
      const href = await noteAttachmentHref(note, { download });
      if (!href) throw new Error("unavailable");
      tab.navigate(href);
    } catch {
      tab.cancel();
      toast.error(t("admin.docs.openFailed", "Could not open the document."));
    }
  }

  async function handleSubmit() {
    if (!canSubmit || !user?.id) return;
    setUploading(true);
    try {
      let file = null as null | {
        url: string;
        name: string | null;
        size: number | null;
        mimeType: string | null;
      };

      if (pendingFile) {
        const scopeKey = paymentId
          ? `payment-${paymentId}`
          : bookingId
            ? `booking-${bookingId}`
            : `user-${userId}`;
        const safeName = pendingFile.name.replace(/[^\w.-]+/g, "_");
        const key = `admin-notes/${scopeKey}/${Date.now()}-${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("tenant-documents")
          .upload(key, pendingFile, {
            contentType: pendingFile.type || "application/octet-stream",
            upsert: false,
          });
        if (upErr) throw upErr;
        const { data } = supabase.storage
          .from("tenant-documents")
          .getPublicUrl(key);
        file = {
          url: data.publicUrl,
          name: pendingFile.name,
          size: pendingFile.size,
          mimeType: pendingFile.type || null,
        };
      }

      await addNote.mutateAsync({
        body: body.trim() || null,
        bookingId,
        userId,
        paymentId,
        createdBy: user.id,
        file,
      });

      setBody("");
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success(t("admin.notes.section.added", "Note added."));
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("admin.notes.section.addFailed", "Failed to add note.")
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <Card className={compact ? "p-3 space-y-3" : "p-5 space-y-4"}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <StickyNote className="h-4 w-4" />
          {resolvedTitle}
        </h3>
        {notes.length > 0 && <Badge variant="outline">{notes.length}</Badge>}
      </div>

      {/* Composer */}
      <div className="space-y-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t(
            "admin.notes.section.placeholder",
            "Add an internal note…"
          )}
          className="min-h-[64px] resize-y text-sm"
        />
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4" />
              {t("admin.notes.attach", "Attach")}
            </Button>
            {pendingFile && (
              <span className="text-xs text-muted-foreground truncate">
                {pendingFile.name}
              </span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) =>
                setPendingFile(e.currentTarget.files?.[0] ?? null)
              }
            />
          </div>
          <Button
            type="button"
            size="sm"
            className="gap-2"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {t("admin.notes.section.addNote", "Add note")}
          </Button>
        </div>
      </div>

      {/* Timeline */}
      {isLoading ? (
        <p className="text-xs text-muted-foreground py-1">
          {t("common.loading", "Loading…")}
        </p>
      ) : notes.length === 0 ? (
        <p className="text-xs text-muted-foreground py-1">
          {t("admin.notes.section.empty", "No notes yet.")}
        </p>
      ) : (
        <ol className="space-y-2.5">
          {notes.map((note) => (
            <li
              key={note.id}
              className="rounded-lg border border-border/60 bg-background p-3 space-y-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-foreground">
                  {authorLabel(
                    note,
                    t("admin.notes.section.adminFallback", "Admin")
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground">
                    {formatWhen(note.created_at)}
                  </span>
                  {canDelete(note) && (
                    <button
                      type="button"
                      aria-label={t(
                        "admin.notes.section.deleteNoteAria",
                        "Delete note"
                      )}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      disabled={deleteNote.isPending}
                      onClick={() => {
                        if (
                          confirm(
                            t(
                              "admin.notes.section.deleteConfirm",
                              "Delete this note?"
                            )
                          )
                        ) {
                          deleteNote.mutate(note.id, {
                            onError: (e) =>
                              toast.error(
                                e instanceof Error
                                  ? e.message
                                  : t(
                                      "admin.notes.section.deleteFailed",
                                      "Failed to delete the note."
                                    )
                              ),
                          });
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              {note.body && (
                <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                  {note.body}
                </p>
              )}
              {note.file_url && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border/60 bg-muted/20 px-2.5 py-1.5">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground/80 min-w-0">
                    <FileText className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">
                      {note.file_name ||
                        t(
                          "admin.notes.section.attachmentFallback",
                          "Attachment"
                        )}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => void openAttachment(note, false)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:underline"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    {t("admin.documents.view", "View")}
                  </button>
                  <button
                    type="button"
                    onClick={() => void openAttachment(note, true)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:underline"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {t("common.download", "Download")}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
